import { FiRefreshCw } from "react-icons/fi";

/**
 * StalenessBadge — an amber "New model version available" pill/banner shown
 * only when the latest run is outdated. The button opens the re-run confirm
 * dialog (wired by the parent page). Renders nothing when not outdated.
 *
 * Amber uses the brand's `health` scale (#FBBF24), reserved for advisory
 * (non-error) callouts.
 */
interface StalenessBadgeProps {
  /** From the run/result's version status. */
  isOutdated?: boolean;
  /** Opens the RerunConfirmDialog. */
  onRerun: () => void;
  /** Current release label, for the tooltip / secondary line. */
  currentModelVersion?: string | null;
  /** "banner" (full-width, prominent) or "pill" (compact inline). */
  variant?: "banner" | "pill";
  /** Disable the button while a run is already queued/running. */
  disabled?: boolean;
  className?: string;
}

export function StalenessBadge({
  isOutdated,
  onRerun,
  currentModelVersion,
  variant = "banner",
  disabled = false,
  className = "",
}: StalenessBadgeProps) {
  if (!isOutdated) return null;

  if (variant === "pill") {
    return (
      <button
        type="button"
        onClick={onRerun}
        disabled={disabled}
        title={
          currentModelVersion
            ? `Current model: ${currentModelVersion}. Re-run to update.`
            : "Re-run to update to the current model."
        }
        className={`inline-flex items-center gap-1.5 rounded-full border border-health-500/40 bg-health-500/15 px-2.5 py-0.5 text-caption font-medium text-health-300 hover:bg-health-500/25 disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${className}`}
      >
        <FiRefreshCw size={12} className="flex-shrink-0" />
        New model version
      </button>
    );
  }

  return (
    <div
      className={`flex flex-wrap items-center justify-between gap-3 rounded-lg border border-health-500/40 bg-health-500/10 px-4 py-3 ${className}`}
    >
      <div className="flex items-start gap-2.5">
        <FiRefreshCw
          size={16}
          className="mt-0.5 flex-shrink-0 text-health-300"
        />
        <div>
          <p className="text-body-sm font-medium text-health-200">
            New model version available
          </p>
          <p className="text-caption text-health-300/80">
            Re-run to update this result to the current model
            {currentModelVersion ? ` (${currentModelVersion})` : ""}.
          </p>
        </div>
      </div>
      <button
        type="button"
        onClick={onRerun}
        disabled={disabled}
        className="inline-flex items-center gap-2 rounded-lg border border-health-500/50 bg-health-500/20 px-3 py-1.5 text-caption font-medium text-health-100 hover:bg-health-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        Re-run to update
      </button>
    </div>
  );
}
